import React, { useState, useEffect, useMemo } from 'react';
import { getUsuarios, crearUsuario, actualizarUsuario, eliminarUsuario, crearGestion, crearSesion, actualizarSesion, eliminarSesion } from '../utils/api';

function formatFechaHora(fechaHora) {
  if (!fechaHora) return '—';
  return new Date(fechaHora).toLocaleString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminPage({ gestiones, onRefresh }) {
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal] = useState(null); // 'usuario' | 'gestion' | 'sesion'
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editSesion, setEditSesion] = useState(null);

  // Solo se puede editar la sesión más reciente (mayor id), y solo mientras
  // su fecha/hora fijada no haya pasado — si ya se llevó a cabo, no tiene
  // sentido seguir editándola.
  const sesionMasRecienteId = useMemo(() => {
    let masReciente = null;
    gestiones.forEach(g => {
      g.sesiones?.forEach(s => { if (!masReciente || s.id > masReciente) masReciente = s.id; });
    });
    return masReciente;
  }, [gestiones]);
  const puedeEditarSesion = (s) =>
    s.id === sesionMasRecienteId && (!s.fecha_hora || new Date(s.fecha_hora) > new Date());

  useEffect(() => { loadUsuarios(); }, []);

  const loadUsuarios = async () => {
    const data = await getUsuarios();
    setUsuarios(data);
  };

  const openModal = (type, defaults = {}) => {
    setForm(defaults); setError(''); setOk('');
    setModal(type);
  };

  // Siguiente número disponible para una gestión + tipo (máximo existente + 1)
  const siguienteNumero = (gestionId, tipo) => {
    const g = gestiones.find(g => String(g.id) === String(gestionId));
    if (!g) return '';
    const nums = (g.sesiones || []).filter(s => s.tipo === tipo).map(s => s.numero);
    return nums.length ? Math.max(...nums) + 1 : 1;
  };

  const closeModal = () => { setModal(null); setEditUser(null); setEditSesion(null); };

  const handleCrearUsuario = async () => {
    try {
      if (editUser) {
        await actualizarUsuario(editUser.id, { ...form, activo: 1 });
        setOk('Usuario actualizado');
      } else {
        await crearUsuario(form);
        setOk('Usuario creado correctamente');
      }
      await loadUsuarios();
      setTimeout(closeModal, 1200);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar usuario');
    }
  };

  const handleDesactivar = async (u) => {
    if (!window.confirm(`¿Desactivar a ${u.nombre}?`)) return;
    await eliminarUsuario(u.id);
    loadUsuarios();
  };

  const handleCrearGestion = async () => {
    if (!form.anio_inicio || !form.anio_fin) return setError('Complete ambos años');
    if (parseInt(form.anio_fin) <= parseInt(form.anio_inicio)) return setError('El año de fin debe ser mayor al de inicio');
    try {
      await crearGestion(form);
      onRefresh(); closeModal();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  };

  const handleCrearSesion = async () => {
    if (!form.gestion_id) return setError('Seleccione una gestión');
    if (!form.numero) return setError('Ingrese el número de sesión');
    try {
      if (editSesion) {
        await actualizarSesion(editSesion.id, form);
        setOk('Sesión actualizada');
      } else {
        await crearSesion(form.gestion_id, form);
      }
      onRefresh(); closeModal();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  };

  const handleEliminarSesion = async (sesion, gestionAnio) => {
    if (!window.confirm(`¿Eliminar sesión N°${sesion.numero} (${sesion.tipo}) de la gestión ${gestionAnio}?\n\nSe eliminarán TODOS los documentos de esta sesión.`)) return;
    try {
      await eliminarSesion(sesion.id);
      onRefresh();
    } catch { }
  };

  return (
    <div className="admin-page">
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 24 }}>Administración</h2>

      {/* Usuarios */}
      <div className="admin-section">
        <h3>
          Usuarios del sistema ({usuarios.length})
          <button className="btn-action btn-red btn-sm" onClick={() => { setEditUser(null); openModal('usuario', { rol: 'asambleista' }); }}>
            + Nuevo usuario
          </button>
        </h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nombre</th><th>Usuario</th><th>Rol</th><th>Estado</th><th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500 }}>{u.nombre}</td>
                  <td style={{ color: '#666' }}>{u.usuario}</td>
                  <td>
                    <span className={`rol-badge ${u.rol === 'admin' ? 'rol-admin' : 'rol-asm'}`}>
                      {u.rol === 'admin' ? 'Admin' : 'Asambleísta'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 12, color: u.activo ? '#166534' : '#999' }}>
                      {u.activo ? '● Activo' : '○ Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="file-btn" onClick={() => {
                        setEditUser(u);
                        openModal('usuario', { nombre: u.nombre, usuario: u.usuario, rol: u.rol });
                      }}>Editar</button>
                      {u.usuario !== 'admin' && (
                        <button className="file-btn red" onClick={() => handleDesactivar(u)}>Desactivar</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gestiones y sesiones */}
      <div className="admin-section">
        <h3>
          Gestiones y sesiones
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-action btn-outline btn-sm" onClick={() => { setEditSesion(null); openModal('sesion', { tipo: 'ordinaria' }); }}>
              + Nueva sesión
            </button>
            <button className="btn-action btn-red btn-sm" onClick={() => openModal('gestion', { anio_inicio: new Date().getFullYear(), anio_fin: new Date().getFullYear()+1 })}>
              + Nueva gestión
            </button>
          </div>
        </h3>
        {gestiones.map(g => (
          <div key={g.id} className="table-wrap" style={{ marginBottom: 16 }}>
            <div style={{ padding: '10px 14px', background: '#f8f8fa', borderBottom: '1px solid #eee', fontWeight: 600, fontSize: 13 }}>
              Gestión {g.anio_inicio}-{g.anio_fin}
            </div>
            <table>
              <thead>
                <tr><th>Tipo</th><th>Número</th><th>Fecha</th><th>Descripción</th><th>Docs</th><th></th></tr>
              </thead>
              <tbody>
                {g.sesiones?.length === 0 && (
                  <tr><td colSpan={6} style={{ color: '#aaa', fontSize: 12, textAlign: 'center' }}>Sin sesiones</td></tr>
                )}
                {g.sesiones?.map(s => (
                  <tr key={s.id}>
                    <td>
                      <span className={`badge ${s.tipo === 'ordinaria' ? 'badge-ord' : 'badge-ext'}`}>
                        {s.tipo === 'ordinaria' ? 'Ordinaria' : 'Extraordinaria'}
                      </span>
                    </td>
                    <td>{String(s.numero).padStart(2,'0')}</td>
                    <td style={{ fontSize: 12 }}>{formatFechaHora(s.fecha_hora)}</td>
                    <td style={{ fontSize: 12, color: '#666' }}>{s.descripcion || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{s.total_docs}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {puedeEditarSesion(s) && (
                          <button className="file-btn" onClick={() => {
                            setEditSesion(s);
                            openModal('sesion', {
                              gestion_id: s.gestion_id, tipo: s.tipo, numero: s.numero,
                              fecha_hora: s.fecha_hora || '',
                              fecha: s.fecha_hora ? s.fecha_hora.split('T')[0] : '',
                              descripcion: s.descripcion || ''
                            });
                          }}>Editar</button>
                        )}
                        <button className="file-btn red" onClick={() => handleEliminarSesion(s, `${g.anio_inicio}-${g.anio_fin}`)}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* ── Modales ── */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modal === 'usuario' && (editUser ? 'Editar usuario' : 'Nuevo usuario')}
                {modal === 'gestion' && 'Nueva gestión'}
                {modal === 'sesion' && (editSesion ? 'Editar sesión' : 'Nueva sesión')}
              </h3>
              <button className="btn-icon" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-err">{error}</div>}
              {ok && <div className="alert alert-ok">{ok}</div>}

              {modal === 'usuario' && (
                <>
                  <div className="form-group">
                    <label>Nombre completo</label>
                    <input value={form.nombre || ''} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Juan Quispe Mamani" />
                  </div>
                  <div className="form-group">
                    <label>Nombre de usuario</label>
                    <input value={form.usuario || ''} onChange={e => setForm(f => ({ ...f, usuario: e.target.value }))} placeholder="Ej: jquispe" disabled={!!editUser} />
                  </div>
                  <div className="form-group">
                    <label>{editUser ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}</label>
                    <input type="password" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
                  </div>
                  <div className="form-group">
                    <label>Rol</label>
                    <select value={form.rol || 'asambleista'} onChange={e => setForm(f => ({ ...f, rol: e.target.value }))}>
                      <option value="asambleista">Asambleísta</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </>
              )}

              {modal === 'gestion' && (
                <>
                  <div className="form-group">
                    <label>Año de inicio</label>
                    <input type="number" value={form.anio_inicio || new Date().getFullYear()} onChange={e => setForm(f => ({ ...f, anio_inicio: e.target.value, anio_fin: parseInt(e.target.value)+1 }))} />
                  </div>
                  <div className="form-group">
                    <label>Año de fin</label>
                    <input type="number" value={form.anio_fin || new Date().getFullYear()+1} onChange={e => setForm(f => ({ ...f, anio_fin: e.target.value }))} />
                  </div>
                </>
              )}

              {modal === 'sesion' && (
                <>
                  <div className="form-group">
                    <label>Gestión{editSesion ? ' (no se puede cambiar)' : ''}</label>
                    <select value={form.gestion_id || ''} disabled={!!editSesion} onChange={e => {
                      const gestion_id = e.target.value;
                      setForm(f => ({ ...f, gestion_id, numero: siguienteNumero(gestion_id, f.tipo || 'ordinaria') }));
                    }}>
                      <option value="">Seleccionar...</option>
                      {gestiones.map(g => <option key={g.id} value={g.id}>Gestión {g.anio_inicio}-{g.anio_fin}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tipo</label>
                    <select value={form.tipo || 'ordinaria'} onChange={e => {
                      const tipo = e.target.value;
                      setForm(f => ({ ...f, tipo, numero: siguienteNumero(f.gestion_id, tipo) }));
                    }}>
                      <option value="ordinaria">Ordinaria</option>
                      <option value="extraordinaria">Extraordinaria</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Número de sesión</label>
                    <input type="number" value={form.numero || ''} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} placeholder="Ej: 1" />
                  </div>
                  <div className="form-group">
                    <label>Fecha y hora de la sesión</label>
                    <input
                      type="datetime-local"
                      value={form.fecha_hora || ''}
                      onChange={e => setForm(f => ({
                        ...f,
                        fecha_hora: e.target.value,
                        fecha: e.target.value ? e.target.value.split('T')[0] : ''
                      }))}
                    />
                    <p style={{fontSize:11,color:'#999',marginTop:4}}>El sistema bloqueará subida/eliminación 48h antes de esta fecha.</p>
                  </div>
                  <div className="form-group">
                    <label>Descripción (opcional)</label>
                    <input value={form.descripcion || ''} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Ej: Sesión de emergencia" />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-action btn-outline" onClick={closeModal}>Cancelar</button>
              <button className="btn-action btn-red" onClick={
                modal === 'usuario' ? handleCrearUsuario :
                modal === 'gestion' ? handleCrearGestion :
                handleCrearSesion
              }>
                {modal === 'usuario' && (editUser ? 'Guardar cambios' : 'Crear usuario')}
                {modal === 'gestion' && 'Crear gestión'}
                {modal === 'sesion' && (editSesion ? 'Guardar cambios' : 'Crear sesión')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
